// The single source of truth for every weather variable on the map.
//
// This lives in src/lib/ so both halves of the codebase import one copy: the
// client bundle pulls it in through esbuild, and the server and preprocessors
// import it by relative path. Everything here must stay environment-neutral —
// no node: builtins, no DOM — because it is type-checked against both.

import { DEFAULT_RECIPE, factorsForRecipe } from './recipe.ts';
import type { Factor, Recipe } from './recipe.ts';

// A colour stop, [value, cssColor].
export type Stop = [number, string];

// A plain-language band, [ltValue, label].
export type Band = [number, string];

// Which chip group a variable appears under.
export type CategoryKey = 'conditions' | 'activities';

// One chip group.
export interface Category {
  key: CategoryKey;
  label: string;
}

// One variable's registry entry.
export interface VariableMeta {
  /** Display name, used by chips, legend and tooltip alike. */
  label: string;
  /** Display unit. */
  unit: string;
  gloss?: string;
  icon?: string;
  /** Which chip group it appears under (see CATEGORIES). */
  category: CategoryKey;
  /**
   * [lo, hi] — the QUANTIZATION range. Changing this invalidates every baked
   * grid (build-grids.ts quantizes to uint8 across it), so it is the one field
   * you cannot edit without a re-bake.
   */
  domain: number[];
  /**
   * Colour stops ordered across the domain. Drives both the MapLibre
   * interpolate expression and the CSS legend gradient, so the map and legend
   * cannot disagree. Safe to edit freely — stops are read at request time and
   * need no re-bake.
   */
  stops?: Stop[];
  /**
   * Plain-language tooltip bands, terminated by Infinity, for variables whose
   * raw number is meaningless to a reader ("8000 m" -> "Light haze").
   */
  bands?: Band[];
  /**
   * IDW cutoff radius — the near-field reach of an observation. Within it the
   * field is genuine interpolation; beyond it, on LAND, build-grids infills the
   * void by extrapolation so the field has no gaps (see infill.ts) — only over
   * ocean does a cell stay UNDEF and draw nothing. Pick from the set already in
   * use ({800, 500, 300}) so build-grids reuses an existing weight matrix.
   */
  cutoffKm?: number;
  /** Decimal places when reporting the value. */
  dp: number;
  /** Stored per-station in data/frames (merge.ts writes it). */
  frame: boolean;
  /** Baked into a dense grid and selectable as a field (build-grids.ts). */
  grid: boolean;
  /** Offered as a chip in the UI. */
  chip: boolean;
  /**
   * Computed by derive.ts per station-year, not read from a report — merge.ts
   * takes it from deriveStation instead of the feature.
   */
  derived?: boolean;
  banded?: boolean;
  /** A 0-100 activity rating; the tooltip renders it as "n/100". */
  score?: boolean;
  custom?: boolean;
  /** Averaged as a unit vector rather than linearly (wind direction). */
  circular?: boolean;
  help?: string;
  factors?: Factor[];
}

export type VarKey =
  | 't' | 'app' | 'd' | 'ws' | 'rain' | 'sky' | 'p' | 'v' | 'c' | 'pr' | 'prate'
  | 'custom' | 'beach' | 'relax' | 'hike' | 'sail' | 'ski' | 'wd';

// One variant group: the chip's label and its members, [varKey, toggleLabel].
export interface VariantGroup {
  label: string;
  members: [VarKey, string][];
}

export const CATEGORIES: Category[] = [
  // 'The weather' is the evidence layer — the raw readings behind each rating — not a
  // co-equal mode; 'Best for' is the evaluative layer a reader starts from.
  { key: 'conditions', label: 'The weather' },
  { key: 'activities', label: 'Best for' },
];

// Variant groups fold near-duplicate variables under a single chip: the chip
// shows the group label, and a segmented toggle underneath swaps which member is
// active. Only the FIRST member of a group carries `chip:true` (it represents the
// chip); the rest are `chip:false`, reachable only through the toggle. This is the
// whole of the "two decisions" redesign — Temperature and Clouds & rain — and app.ts
// reads it to build the picker. Members are [varKey, toggleLabel].
export const VARIANT_GROUPS = {
  temp: { label: 'Temperature', members: [['t', 'Actual'], ['app', 'Feels like']] },
  wet: {
    label: 'Clouds & rain',
    members: [
      ['sky', 'Cloud'],
      ['v', 'Haze & fog'],
      ['rain', 'Rain'],
      ['pr', 'Rainfall'],
      ['d', 'Humidity'],
    ],
  },
} satisfies Record<string, VariantGroup>;

// A goodness ramp shared by every 0-100 activity score. The scores are `banded`
// (see below), so this ramp is sampled at the four suitability-band midpoints —
// 17.5 Poor (red), 45 Fair (amber), 67.5 Good (light green), 90 Ideal (green) —
// to colour each flat band. The stops sit ON those midpoints so bandScale/colorAt
// return the exact band colour. One ramp so the four scores read on the same
// scale — a "Good" for the beach and a "Good" for sailing are the same green.
const SCORE_STOPS: Stop[] = [
  [0, '#d73027'],
  [17.5, '#d73027'],
  [45, '#fdae61'],
  [67.5, '#a6d96a'],
  [90, '#1a9850'],
  [100, '#1a9850'],
];

// The four-level suitability bands shared by every activity score: the 0-100
// number reduced to "how suitable is a typical such day". Exported because the
// similarity search reuses the "Good" floor as its mode filter (match.ts).
export const SCORE_BANDS: Band[] = [
  [35, 'Poor'],
  [55, 'Fair'],
  [80, 'Good'],
  [Infinity, 'Ideal'],
];

// Declaration order is load-bearing: it fixes the order of SCALARS (and so of
// manifest.variables and manifest.stats), of FIELD_VARS (bake order), and of the
// chips. Keep it stable.
export const VAR: Record<VarKey, VariableMeta> = {
  // Temperature (°C) — reversed RdYlBu, diverging near freezing.
  t: {
    label: 'Temperature',
    unit: '°C',
    category: 'conditions',
    domain: [-40, 48],
    cutoffKm: 800,
    dp: 1,
    frame: true,
    grid: true,
    chip: true,
    stops: [
      [-40, '#313695'],
      [-30, '#4575b4'],
      [-20, '#74add1'],
      [-10, '#abd9e9'],
      [0, '#e0f3f8'],
      [5, '#ffffbf'],
      [12, '#fee090'],
      [20, '#fdae61'],
      [30, '#f46d43'],
      [40, '#d73027'],
      [48, '#a50026'],
    ],
  },
  // Feels like (°C) — Australian Apparent Temperature. Derived in merge.ts from
  // t, d and ws per station-year, never from the blended means (see src/lib/derive.js).
  //
  // Deliberately shares `t`'s ramp, stretched to its slightly wider domain. The
  // whole point of this variable is the DIFFERENCE from the temperature — where
  // wind or humidity move the number and by how much — and that comparison only
  // reads if the two maps use the same colours for the same degrees.
  app: {
    label: 'Feels like',
    unit: '°C',
    category: 'conditions',
    domain: [-45, 50],
    cutoffKm: 800, // the same smooth thermal field as t/d, and reuses their matrix
    dp: 1,
    frame: true,
    grid: true,
    chip: false, // reached through the Temperature chip's variant toggle, not a chip of its own
    derived: true,
    banded: true, // shown as comfort categories, not a continuous number (Actual temperature stays continuous)
    stops: [
      [-45, '#313695'],
      [-30, '#4575b4'],
      [-20, '#74add1'],
      [-10, '#abd9e9'],
      [0, '#e0f3f8'],
      [5, '#ffffbf'],
      [12, '#fee090'],
      [20, '#fdae61'],
      [30, '#f46d43'],
      [40, '#d73027'],
      [50, '#a50026'],
    ],
    bands: [
      [0, 'Freezing'],
      [10, 'Cold'],
      [16, 'Cool'],
      [22, 'Mild'],
      [28, 'Warm'],
      [Infinity, 'Hot'],
    ],
  },
  // Humidity — dew point (°C), presented as what it means for a reader: how
  // sticky the air feels. The bands below carry the actual scale ("Muggy",
  // "Oppressive"); the label is just the word people look for.
  //
  // Dew point rather than relative humidity, deliberately. RH is confounded by
  // temperature and misleads badly at both ends — 90% RH at 5°C is not muggy, 70%
  // at 30°C is oppressive. Dew point is an absolute measure of how much moisture
  // is in the air and maps straight onto the standard comfort scale below, which
  // is why forecasters use it for this. It is also already merged and gridded, so
  // this costs nothing but a label.
  //
  // The ramp is rebuilt around the comfort bands rather than the old generic
  // thermal one: the interesting range is 10–24°C, where the whole scale runs from
  // "pleasant" to "unbearable", and the old ramp spent one stop on it.
  d: {
    label: 'Humidity',
    unit: '°C',
    // Names the measured quantity in the tooltip when the bare unit would mislead:
    // humidity is a dew point, and a °C value next to the air temperature otherwise
    // reads as a second temperature (or as "feels like"). Rendered as "dew point 16 °C".
    gloss: 'dew point',
    category: 'conditions',
    domain: [-40, 30],
    cutoffKm: 800,
    dp: 1,
    frame: true,
    grid: true,
    chip: false, // folded into the Clouds & rain chip's variant toggle
    banded: true,
    stops: [
      [-40, '#4a2c8f'],
      [-10, '#5e72c4'],
      [5, '#8bc4d9'],
      [10, '#d9f0e3'],
      [13, '#eff9d6'],
      [16, '#fdf0a6'],
      [18, '#fdcc74'],
      [21, '#f79246'],
      [24, '#e04d38'],
      [30, '#8c1f18'],
    ],
    bands: [
      [10, 'Dry'],
      [13, 'Comfortable'],
      [16, 'Noticeable'],
      [18, 'Sticky'],
      [21, 'Muggy'],
      [24, 'Oppressive'],
      [Infinity, 'Sweltering'],
    ],
  },
  // Wind speed (m/s) — YlGnBu, calm→gale. Not a chip: wind is a vector, so it is
  // drawn as the arrow overlay (addWindLayer in app.ts) riding on top of whichever
  // scalar is selected. The label and unit are still needed by the tooltip and the
  // arrow legend, and the grid is still baked to place the arrows.
  ws: {
    label: 'Wind speed',
    unit: 'm/s',
    category: 'conditions',
    domain: [0, 30],
    cutoffKm: 500,
    dp: 1,
    frame: true,
    grid: true,
    chip: false,
    stops: [
      [0, '#ffffd9'],
      [3, '#c7e9b4'],
      [6, '#7fcdbb'],
      [10, '#41b6c4'],
      [15, '#2c7fb8'],
      [20, '#253494'],
      [30, '#081d58'],
    ],
  },
  // Chance of rain (%) — the fraction of years that were WET at this calendar day
  // and time bucket. Derived in merge.ts: each year votes 100 (wet) or 0 (dry),
  // and the weighted mean of that indicator IS the frequency, for free, through
  // the same machinery every scalar uses.
  //
  // "Wet" is depth ≥ 0.2mm, counted ONLY from 1-hour precip reports. Mixing
  // accumulation periods would let a 6-hourly station read three times wetter than
  // an hourly neighbour purely from a longer catch window, and the interpolation
  // would paint that reporting-protocol difference as a weather front.
  rain: {
    label: 'Chance of rain',
    unit: '%',
    category: 'conditions',
    domain: [0, 100],
    cutoffKm: 300,
    dp: 0,
    frame: true,
    grid: true,
    chip: false, // folded into the Clouds & rain chip's variant toggle
    derived: true,
    banded: true,
    stops: [
      [0, '#ffffe5'],
      [15, '#f7fcb9'],
      [30, '#d9f0a3'],
      [45, '#addd8e'],
      [60, '#41ab5d'],
      [80, '#238443'],
      [100, '#005a32'],
    ],
    bands: [
      [10, 'Dry'],
      [30, 'Unlikely'],
      [55, 'Showery'],
      [80, 'Likely'],
      [Infinity, 'Wet'],
    ],
  },
  // Sky cover (%) — how much of the sky is cloud, 0 clear to 100 overcast. Parsed
  // from the GF1/GA sky-condition groups (see decodeSky), replacing the cloud
  // ceiling that used to stand in for "is it sunny". Not derived — it is a real
  // measurement, merged like temperature.
  sky: {
    label: 'Sky cover',
    unit: '%',
    category: 'conditions',
    domain: [0, 100],
    cutoffKm: 300,
    dp: 0,
    frame: true,
    grid: true,
    chip: true, // primary member of the Clouds & rain group — this is the chip
    banded: true,
    stops: [
      [0, '#2b6cb0'],
      [20, '#63a3d0'],
      [45, '#a9c7de'],
      [65, '#cbd4da'],
      [85, '#9aa3ab'],
      [100, '#6b727a'],
    ],
    bands: [
      [12.5, 'Clear'],
      [37.5, 'Few clouds'],
      [62.5, 'Scattered'],
      [87.5, 'Broken'],
      [Infinity, 'Overcast'],
    ],
  },
  // Sea-level pressure (hPa) — PuOr, diverging around 1013.
  //
  // Not a chip: it is a meteorologist's variable, not a holidaymaker's. Nothing a
  // reader wants to know about a day is answered by 1013 hPa, and the number is
  // read as jargon. Still merged into the frames — the data is free and the field
  // is one flag away if it is ever wanted.
  p: {
    label: 'Sea-level pressure',
    unit: 'hPa',
    category: 'conditions',
    domain: [965, 1045],
    cutoffKm: 1200,
    dp: 1,
    frame: true,
    grid: false,
    chip: false,
    stops: [
      [965, '#2d004b'],
      [985, '#8073ac'],
      [1000, '#b2abd2'],
      [1013, '#f7f7f7'],
      [1025, '#fdb863'],
      [1035, '#e08214'],
      [1045, '#7f3b08'],
    ],
  },
  // Haze & Fog — visibility (m), presented as what it costs you: the view.
  //
  // The domain stays [0, 20000] because that is what the grids are quantized
  // against, but the stops are pulled down into the range that matters. The old
  // ramp spent 60% of its length between 8 km and 20 km, where the difference is
  // invisible to anyone who is not landing a plane; everything a walker cares
  // about happens below 4 km.
  v: {
    label: 'Haze & Fog',
    unit: 'm',
    category: 'conditions',
    domain: [0, 20000],
    cutoffKm: 300,
    dp: 0,
    frame: true,
    grid: true,
    chip: false, // folded into the Clouds & rain chip's variant toggle
    banded: true,
    stops: [
      [0, '#6b1e3c'],
      [200, '#a50026'],
      [1000, '#e34a33'],
      [4000, '#fdae61'],
      [10000, '#d9ed92'],
      [14000, '#66bd63'],
      [20000, '#1a9850'],
    ],
    bands: [
      [200, 'Dense fog'],
      [1000, 'Fog'],
      [4000, 'Mist / haze'],
      [10000, 'Light haze'],
      [Infinity, 'Clear'],
    ],
  },
  // Cloud ceiling (m) — viridis, low→high.
  //
  // Not a chip: an aviation metric, and only ever on the map because real sky
  // COVER was never parsed. Ceiling is the height of the cloud base, not how much
  // sky is covered — it cannot answer "will it be sunny", which is the question it
  // was standing in for. `sky` replaces it.
  c: {
    label: 'Cloud ceiling',
    unit: 'm',
    category: 'conditions',
    domain: [0, 12000],
    cutoffKm: 400,
    dp: 0,
    frame: true,
    grid: false,
    chip: false,
    stops: [
      [0, '#440154'],
      [1000, '#3b528b'],
      [2500, '#21918c'],
      [4500, '#5ec962'],
      [8000, '#fde725'],
      [12000, '#fdff9e'],
    ],
  },
  // Precipitation (mm) — white→deep teal/blue. Rendered as amount categories, and
  // reached through the Clouds & rain chip's "Rainfall" variant (the depth twin of
  // the "Rain" chance). The bands are the average depth per report, so the
  // thresholds sit low; the last catches genuine downpours.
  pr: {
    label: 'Precipitation',
    unit: 'mm',
    category: 'conditions',
    domain: [0, 40],
    cutoffKm: 250,
    dp: 1,
    frame: true,
    grid: true,
    chip: false, // folded into the Clouds & rain chip's variant toggle
    banded: true,
    stops: [
      [0, '#f7fbff'],
      [0.5, '#d0d1e6'],
      [2, '#a6bddb'],
      [5, '#67a9cf'],
      [10, '#3690c0'],
      [20, '#02818a'],
      [40, '#016450'],
    ],
    bands: [
      [0.2, 'None'],
      [1, 'Light'],
      [4, 'Moderate'],
      [10, 'Heavy'],
      [Infinity, 'Downpour'],
    ],
  },
  // Rain RATE (mm/h) — derived per report as depth / accumulation-window (see
  // derive.ts), then section-averaged. Not a user-facing map layer (no chip, no
  // palette); it exists only so a city's series can reconstruct a dekad's CUMULATIVE
  // rainfall (rate × 24h × days-in-dekad), which raw depth can't give without the
  // mixed-window bias. Baked as a field purely so the series sampler can read it off
  // the same grids as everything else.
  prate: {
    label: 'Rain rate',
    unit: 'mm/h',
    category: 'conditions',
    domain: [0, 3],
    cutoffKm: 250,
    dp: 3,
    frame: true,
    grid: true,
    derived: true,
    chip: false,
  },
  // --- Activity scores (0-100) --------------------------------------------------
  // Each is a per-year, per-station comfort score for one activity, averaged
  // across the years (see src/lib/derive.js). Mean-of-scores, not score-of-means:
  // the number is how good a typical such day is, not how good the average day
  // would be — and the average day, calm and mild and never actually occurring,
  // is not the one you plan a trip around.

  // My kind of day — the user-defined score, declared FIRST among the activities so
  // that when it shows it leads the "Good for" bar and the calendar: it is the
  // reader's own lens, ahead of the curated five. It is hidden until "unlocked"
  // — the chip only renders once the user has made it theirs (edited a recipe,
  // or arrived on a shared ?r= link); before that its default is a clone of an
  // existing score and there is no distinct data to show (see activityKeys /
  // unlockCustom in app.ts).
  //
  // Not baked: there is no grid and no frame column, so `grid:false` keeps it
  // out of every preprocess list, and the `custom` flag is what the server and
  // app.ts special-case instead. Field tiles are computed at request time from
  // the component grids (app, ws, rain, sky, v) against a recipe carried in the
  // tile URL (see src/lib/recipe.js); city dots and series are scored
  // client-side from the component values they already carry. That makes it
  // score-of-MEANS where the baked five are mean-of-scores — softer in variable
  // climates, exact on the rain factor — which the help text owns up to.
  //
  // `factors` here is the DEFAULT recipe's drivers, and a fallback only. The
  // live ones depend on the recipe currently in force, so anything explaining a
  // custom score resolves them through factorsFor(key, recipe) below rather than
  // reading this frozen registry.
  custom: {
    label: 'My kind of day',
    icon: '✨',
    unit: '/100',
    category: 'activities',
    domain: [0, 100],
    dp: 0,
    frame: false,
    grid: false,
    chip: true,
    custom: true,
    score: true,
    banded: true,
    stops: SCORE_STOPS,
    bands: SCORE_BANDS,
    help: 'Your own rating. Pick what gates it — feels-like or wind — set the ideal range, and weigh how much rain, wind and sky matter. Computed live from the same maps as the other ratings, from each place’s typical conditions.',
    factors: factorsForRecipe(DEFAULT_RECIPE),
  },
  // Sunbathing — hot feels-like gates, wind/rain/sun modulate. A climate score, not
  // a place: it answers "is the weather right for lying in the sun", wherever you are.
  // The key is `beach` for history; the label deliberately claims no coastline (the
  // field is masked to land, not to a shoreline, so it renders inland too).
  beach: {
    label: 'Sunbathing',
    icon: '🌞',
    unit: '/100',
    category: 'activities',
    domain: [0, 100],
    cutoffKm: 300,
    dp: 0,
    frame: true,
    grid: true,
    chip: true,
    derived: true,
    score: true,
    banded: true,
    stops: SCORE_STOPS,
    bands: SCORE_BANDS,
    help: 'Warm feels-like gates the score — best 26–32 °C. Dry air, light wind and clear skies each lift it; none alone rules it out.',
    // Each factor is checked in three bands: `good` is the ideal plateau (✓), `ok`
    // is where the score is still decent (~), outside that is poor (✗). The ranges
    // trace deriveStation.beach in src/lib/derive.js (gate trap(app,22,26,32,36);
    // dry, calm, sunny modulate) but the `good`/`ok` split is tighter than "score
    // > 0", so a value already in the fall-off reads as a warning, not a tick. Open
    // ends are null. `low`/`high` give the reason when a value falls the wrong side.
    factors: [
      { v: 'app', label: 'warm', low: 'too cool', high: 'too hot', good: [26, 32], ok: [24, 34] },
      { v: 'rain', label: 'dry', high: 'often wet', good: [0, 20], ok: [0, 55] },
      { v: 'ws', label: 'light wind', high: 'too windy', good: [0, 5], ok: [0, 12] },
      { v: 'sky', label: 'sunny', high: 'cloudy', good: [0, 30], ok: [0, 75] },
    ],
  },
  // Easy outdoors — a comfortable feels-like gates a relaxed day out (strolling,
  // sitting out, sightseeing). Warmer band than Active outdoors because you are not
  // exerting, and sun is a bonus rather than a gate — the two differences from hike
  // and beach that earn it its own tab. Location-blind like the rest: it rates the
  // weather, not whether there is a café or a promenade.
  relax: {
    label: 'Easy outdoors',
    icon: '🌤️',
    unit: '/100',
    category: 'activities',
    domain: [0, 100],
    cutoffKm: 300,
    dp: 0,
    frame: true,
    grid: true,
    chip: true,
    derived: true,
    score: true,
    banded: true,
    stops: SCORE_STOPS,
    bands: SCORE_BANDS,
    help: 'A comfortable feels-like gates the score — best 16–26 °C for an easy day out (strolling, sitting out, sightseeing). Dryness matters most; strong wind knocks it back. Sun is a bonus, not required.',
    // Mirrors deriveStation.relax: gate trap(app,12,16,26,30); dry dominant, calm
    // modulates. Warmer plateau than hike (16–26 vs 10–20) — the effort axis. `ok`
    // stops at 30, matching the gate's far edge.
    factors: [
      { v: 'app', label: 'comfortable', low: 'too cold', high: 'too hot', good: [16, 26], ok: [12, 30] },
      { v: 'rain', label: 'dry', high: 'often wet', good: [0, 20], ok: [0, 55] },
      { v: 'ws', label: 'light wind', high: 'windy', good: [0, 6], ok: [0, 14] },
    ],
  },
  // Active outdoors — a cool feels-like gates (you generate heat exerting), dryness
  // matters most, wind modulates. Cooler plateau than a relaxed day out (10–20 vs
  // 16–26) — that offset is the effort axis the two share.
  hike: {
    label: 'Active outdoors',
    icon: '🥾',
    unit: '/100',
    category: 'activities',
    domain: [0, 100],
    cutoffKm: 300,
    dp: 0,
    frame: true,
    grid: true,
    chip: true,
    derived: true,
    score: true,
    banded: true,
    stops: SCORE_STOPS,
    bands: SCORE_BANDS,
    help: 'A mild feels-like gates the score — best 10–20 °C, cool enough for hiking, cycling and running. Dryness matters most, with a penalty for strong wind.',
    // Mirrors deriveStation.hike: gate trap(app,2,10,20,27); dry dominant, calm modulates.
    // `ok` stops at 24, not 27, so a Madrid-hot 26 °C reads "too warm ✗" rather than a
    // misleading tick — the score there is already near zero.
    factors: [
      { v: 'app', label: 'mild', low: 'too cold', high: 'too warm', good: [10, 20], ok: [5, 24] },
      { v: 'rain', label: 'dry', high: 'often wet', good: [0, 20], ok: [0, 60] },
      { v: 'ws', label: 'light wind', high: 'windy', good: [0, 6], ok: [0, 14] },
    ],
  },
  // Wind sports — the one score where WIND is the gate, not a spoiler. Beaufort
  // 3-4 is the sweet spot; warmth only modulates (you dress for it), and visibility
  // matters as safety. The inverse of beach on wind, which is what makes it worth
  // having rather than a re-skin.
  sail: {
    label: 'Wind sports',
    icon: '💨',
    unit: '/100',
    category: 'activities',
    domain: [0, 100],
    cutoffKm: 300,
    dp: 0,
    frame: true,
    grid: true,
    chip: true,
    derived: true,
    score: true,
    banded: true,
    stops: SCORE_STOPS,
    bands: SCORE_BANDS,
    help: 'A steady breeze gates the score — Beaufort 3–4 (about 2–11 m/s) is ideal for sailing, kiting and windsurfing. Warmth matters less (you dress for it); good visibility and dry air modulate.',
    // Mirrors deriveStation.sail: gate trap(ws,2,3.5,8,11); warm ramp(app,4,14),
    // clear ramp(v,1000,4000), dry. Wind is the gate, so its band decides the fit.
    factors: [
      { v: 'ws', label: 'good breeze', low: 'too calm', high: 'too strong', good: [3.5, 8], ok: [2.5, 10] },
      { v: 'app', label: 'warm enough', low: 'too cold', good: [14, null], ok: [6, null] },
      { v: 'v', label: 'clear view', low: 'poor visibility', good: [4000, null], ok: [1500, null] },
      { v: 'rain', label: 'dry', high: 'wet', good: [0, 30], ok: [0, 70] },
    ],
  },
  // Freeze + snowfall likelihood — NOT "ski conditions". Snow depth is ~1%
  // reported, so this is a proxy: cold enough to hold snow, and snowfall likely.
  // Read it with the caveat that the interpolation ignores elevation while the
  // stations are valley-floor airports, so it is least trustworthy in exactly the
  // mountains a skier would look at.
  ski: {
    label: 'Snow',
    icon: '❄️',
    unit: '/100',
    category: 'activities',
    domain: [0, 100],
    cutoffKm: 300,
    dp: 0,
    frame: true,
    grid: true,
    chip: true,
    derived: true,
    score: true,
    banded: true,
    stops: SCORE_STOPS,
    bands: SCORE_BANDS,
    help: 'Cold enough to hold snow with snowfall likely — a proxy, not measured depth. Least reliable in the mountains, where valley-floor stations under-read (see the note).',
    // Mirrors deriveStation.ski: cold gate (1 - ramp(app,-2,4)); snow needs a cold, wet report.
    factors: [
      { v: 'app', label: 'freezing', high: 'too mild', good: [null, -2], ok: [null, 2] },
      { v: 'rain', label: 'snow likely', low: 'too dry for snow', good: [35, 100], ok: [10, 100] },
      { v: 'ws', label: 'light wind', high: 'windy', good: [0, 6], ok: [0, 14] },
    ],
  },
  // Wind direction (°). Circular, so it is never averaged linearly — merge.ts
  // takes a unit-vector mean and build-grids.ts interpolates sin/cos separately
  // (350° and 10° must blend to 0°, not 180°). Present here only so its rounding
  // lives with every other variable's; it is not a scalar, field or chip.
  wd: {
    label: 'Wind direction',
    unit: '°',
    category: 'conditions',
    domain: [0, 360],
    dp: 0,
    frame: true,
    grid: false,
    chip: false,
    circular: true,
  },
};

const keys = Object.keys(VAR) as VarKey[];

// Chips, in display order.
export const VARIABLES: VarKey[] = keys.filter((k) => VAR[k].chip);
// Scalars merge.ts blends as a weighted mean and writes into data/frames.
export const SCALARS: VarKey[] = keys.filter((k) => VAR[k].frame && !VAR[k].circular);
// Everything stored per-station in a frame, including the circular ones.
export const FRAME_VARS: VarKey[] = keys.filter((k) => VAR[k].frame);
// Scalars baked into a dense grid and servable as a colour field.
export const FIELD_VARS: VarKey[] = keys.filter((k) => VAR[k].grid);
// Computed by src/lib/derive.js rather than read from a station's report. merge.ts
// needs the distinction: everything else it looks up on the feature, these it
// works out. See derive.ts for why they are computed per-year rather than after
// the blend.
export const DERIVED_VARS: VarKey[] = keys.filter((k) => VAR[k].derived);

export const DP: Record<string, number> = Object.fromEntries(FRAME_VARS.map((k) => [k, VAR[k].dp] as const));
// cutoffKm is optional on the entry but mandatory once `grid` is set — variables.test.js
// asserts every gridded variable carries one.
export const CUTOFF_KM: Record<string, number> = Object.fromEntries(FIELD_VARS.map((k) => [k, VAR[k].cutoffKm!] as const));
// One variable as it appears in manifest.variables.
export interface VariableManifestMeta {
  label: string;
  unit: string;
  domain: number[];
}

// What merge.ts advertises in manifest.variables. build-grids.ts reads the
// domain back from the manifest rather than from here — deliberately, so grids
// can only ever be quantized against the domain the data was actually merged
// under. Editing a domain and re-baking without re-merging is then impossible
// rather than silently wrong.
export const VARIABLE_META: Record<string, VariableManifestMeta> = Object.fromEntries(
  SCALARS.map((k) => [k, { label: VAR[k].label, unit: VAR[k].unit, domain: VAR[k].domain }] as const)
);

// The drivers to explain a score by. Every baked rating states its own; the
// custom one's depend on the recipe in force, which is not a property of the
// registry — so callers pass the recipe rather than the registry being edited to
// match it.
export function factorsFor(varKey: VarKey, recipe: Recipe): Factor[] {
  return VAR[varKey] && VAR[varKey].custom ? factorsForRecipe(recipe) : VAR[varKey]?.factors || [];
}

// The registry is data, and shared by the browser, the server and the pipeline.
// Freezing it makes that structural: a runtime edit here would have been visible
// to every one of them, in whichever order they happened to read it.
Object.freeze(VAR);
for (const entry of Object.values(VAR)) Object.freeze(entry);
